///
/// EarthBrowser
///
/// Created by Matt Giger on 9/19/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Loads lossless WebP elevation tiles: each 16-bit elevation value is
/// packed as R = high byte, G = low byte, and the tile metadata (child tile
/// existence, min/max elevation) rides in a trailing custom RIFF chunk with
/// fourCC "elev". Decoding uses the browser's native WebP decoder via
/// createImageBitmap, replacing the hand-rolled 16-bit PNG decoder and its
/// worker pool: the RIFF metadata scan is a few microseconds on the main
/// thread and the image decode happens on the browser's internal threads.
///

import { logger } from "@/utils/LogDispatcher"
import { type Texture } from "@stovokor/shader/Texture"
import { DataLoader } from "@/earthbrowser/utils/loaders/DataLoader"

/** Per-tile info embedded by the tile build pipeline */
export interface TileElevationMetadata {
	z: number
	y: number
	x: number
	children: [boolean, boolean, boolean, boolean]
	max: number
	min: number
}

/**
 * Extract the "elev" metadata chunk from a WebP (RIFF) buffer without
 * decoding the image. Returns undefined when absent or not a RIFF file.
 */
function readElevationMetadata(buffer: ArrayBuffer): TileElevationMetadata | undefined {
	const view = new DataView(buffer)
	if(buffer.byteLength < 12 || view.getUint32(0, false) !== 0x52494646) { // "RIFF"
		return undefined
	}

	let offset = 12
	while(offset + 8 <= buffer.byteLength) {
		const fourcc = view.getUint32(offset, false)
		const size = view.getUint32(offset + 4, true)
		if(fourcc === 0x656c6576) { // "elev"
			const bytes = new Uint8Array(buffer, offset + 8, Math.min(size, buffer.byteLength - offset - 8))
			try {
				// strip RIFF even-padding NUL before parsing
				const text = new TextDecoder().decode(bytes).replace(/\0+$/, '')
				return JSON.parse(text) as TileElevationMetadata
			} catch {
				return undefined
			}
		}
		offset += 8 + size + (size % 2)
	}
	return undefined
}

/**
 * ElevationLoader loads packed-WebP elevation tiles either directly into a
 * GPU texture (fast path, no CPU-side pixel access) or into a Uint16Array
 * for CPU consumers (base elevation queries, contour generation).
 */
export class ElevationLoader extends DataLoader {
	/** Indicates if the image decoded successfully */
	public valid: boolean = false
	/** Width of the loaded image */
	public width: number = 0
	/** Height of the loaded image */
	public height: number = 0
	/** Tile metadata from the embedded "elev" RIFF chunk */
	public elevationMetadata?: TileElevationMetadata

	/**
	 * Loads the tile into a texture as packed RGB8 data. The shader
	 * reconstructs meters from the R/G bytes.
	 * @returns true when the texture was uploaded successfully
	 */
	public async loadIntoTexture(texture: Texture): Promise<boolean> {
		texture.available = false

		const bitmap = await this.loadBitmap()
		if(!bitmap) {
			return false
		}
		if(this._aborted) {
			bitmap.close()
			return false
		}

		texture.uploadDataBitmap(bitmap)
		bitmap.close()
		return true
	}

	/**
	 * Loads the tile into a Uint16Array of elevation values for CPU-side
	 * consumers, via an OffscreenCanvas readback of the decoded bitmap.
	 */
	public async loadData(): Promise<Uint16Array | undefined> {
		const bitmap = await this.loadBitmap()
		if(!bitmap) {
			return undefined
		}

		const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
		const ctx = canvas.getContext('2d', { willReadFrequently: true })
		if(!ctx) {
			bitmap.close()
			logger.warn('ElevationLoader: no 2d context for readback')
			return undefined
		}
		ctx.drawImage(bitmap, 0, 0)
		const image = ctx.getImageData(0, 0, bitmap.width, bitmap.height)
		bitmap.close()
		// detach the canvas backing store now instead of waiting for GC
		canvas.width = 0
		canvas.height = 0

		const pixels = image.data
		const data = new Uint16Array(this.width * this.height)
		for(let i = 0; i < data.length; i++) {
			data[i] = (pixels[i * 4] << 8) | pixels[i * 4 + 1]
		}
		return this._aborted ? undefined : data
	}

	/**
	 * Fetch (with CacheTable caching from DataLoader), extract the metadata
	 * chunk and decode the WebP with the browser's native decoder. The
	 * bitmap decode options disable premultiply and color management so the
	 * packed bytes come through untouched.
	 */
	private async loadBitmap(): Promise<ImageBitmap | undefined> {
		this.valid = false

		const buffer = await this.load()
		if(!buffer || this._aborted) {
			return undefined
		}

		try {
			this.elevationMetadata = readElevationMetadata(buffer)
			const blob = new Blob([buffer], { type: 'image/webp' })
			const bitmap = await createImageBitmap(blob, {
				premultiplyAlpha: 'none',
				colorSpaceConversion: 'none',
			})
			this.width = bitmap.width
			this.height = bitmap.height
			this.valid = true
			return bitmap
		} catch (error) {
			// warn (not error) so bad tile payloads don't become Sentry events
			logger.warn('Error decoding elevation tile:', error)
			return undefined
		}
	}
}
